import { Component, Inject, Injector, Input, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Attendance, Card, CardPriorityEnum, CardService, Contact, Customer, CustomerService, FirebaseService, Funnel, FunnelService, LossReason, PRIORITY_LABEL, Product, ProductService, Stage, StageKindEnum, StageService, UploadTypeEnum, User, UserService, moneyMaskOutput } from 'lib-trend-core';
import { Validators } from '@angular/forms';
import { BehaviorSubject, finalize, forkJoin, takeUntil } from 'rxjs';
import { CustomerQuickFormComponent } from '../customer-quick-form/customer-quick-form.component';
import { ProductQuickFormComponent } from '../product-quick-form/product-quick-form.component';
import { SelectLossReasonComponent, SelectLossReasonData, SelectLossReasonResult } from '../loss-reason/select-loss-reason/select-loss-reason.component';

export interface CardFormComponentData {
  idStage: string;
  idFunnel: string;
  card: Card;
  attendance?: Attendance;
}

export interface Attachments {
  filename: string;
  contentType: string;
  size: number;
  url: string;
}
@Component({
    selector: 'app-card-form',
    templateUrl: './card-form.component.html',
    styleUrl: './card-form.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CardFormComponent extends AbstractComponent {
  /** ngx-mask hands the money inputs a string; the model keeps a number. */
  protected readonly moneyMaskOutput = moneyMaskOutput;

  @Input() stages: Array<Stage>;
  priorities = Object.values(CardPriorityEnum).map(v => ({ value: v, label: PRIORITY_LABEL[v] }));
  public listUser: Array<User> = new Array<User>();

  public card: Card;

  public customers: Array<Customer> = [];
  public listCustomer: Array<Customer> = new Array<Customer>();

  public products: Array<Product> = new Array<Product>();
  public listProduct: Array<Product> = new Array<Product>();

  public customerControl: FormControl = new FormControl('');
  public productControl: FormControl = new FormControl('');

  private estimatedValueEdited = false;

  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();

  public file: File;
  public fileLoaded = false;
  public bytes: string;
  public attachments: Array<Attachments> = [];

  public isCardConverting = false;
  public funnels: Array<Funnel> = [];
  public funnelStages: Array<Stage> = [];
  public stagesList: Array<Stage> = [];

  constructor(
    injector: Injector,
    @Inject(MAT_DIALOG_DATA) public data: CardFormComponentData,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<CardFormComponent>,
    private customerService: CustomerService,
    private cardService: CardService,
    private userService: UserService,
    private productService: ProductService,
    public firebaseService: FirebaseService,
    private funnelService: FunnelService,
    private stageService: StageService,
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
    this.isNew = !this.data.card?._id;

    forkJoin([
      this.userService.getList(),
      this.customerService.getList(),
      this.productService.getList(),
      this.funnelService.getList(),
      this.stageService.getList(),
    ]).pipe(takeUntil(this.destroy$)).subscribe({
      next: ([users, customers, products, funnels, stages]) => {
        this.listUser = users;
        this.customers = customers;
        this.listCustomer = customers;
        this.funnels = funnels;
        this.stagesList = stages;
        this.products = products;
        this.listProduct = products;

        this.setupFilters();
        this.setupEstimatedValueAutoFill();
        this.applyCustomerFilter(this.customerControl.value);
        this.applyProductFilter(this.productControl.value);

        if (this.isNew) {
          this.card = {} as Card;
          if (this.data.attendance) {
            this.isCardConverting = true;
            this.populateConversationWithData();
          }
        } else {
          this.populateFormWithData();
        }
      }
    });
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      name: [null, [Validators.required]],
      description: [null],
      attendance: [null],
      priority: [CardPriorityEnum.MEDIUM, [Validators.required]],
      // Temperature is derived by the recompute (E2 RF-22) — never sent from here.
      includedAt: [null],
      funnel: [this.data.idFunnel, [Validators.required]],
      stage: [this.data.idStage, [Validators.required]],
      customer: [null],
      isPrivate: [this.data.card?.isPrivate ?? false, [Validators.required]],
      budget: [this.data.card?.budget ?? 0, [Validators.required]],
      estimatedValue: [this.data.card?.budget ?? 0, [Validators.required]],
      product: [this.data.card?.product ?? []],
      contact: [null],
      order: [0],
      user: [null],
      attachments: [this.attachments ?? []],
    });

    this.formGroup.valueChanges.subscribe((value) => {
      Object.assign(this.card, value);
    });
  }

  private populateFormWithData(): void {
    this.card = structuredClone(this.data.card);
    this.formGroup.patchValue({
      ...this.card,
      customer: this.card.customer?._id ?? null,
      user: this.card.user?._id ?? null,
      attendance: this.card.attendance?._id ?? null,
    }, { emitEvent: false });
    this.attachments = this.card.attachments ?? [];
    this.estimatedValueEdited = false;
    this.syncSelectedProducts();
  }

  private populateConversationWithData(): void {
    if (!this.data.attendance) return;

    const contact: Contact = this.data.attendance.contact;

    /**
     * The account is resolved SERVER-SIDE from the conversation's contact (E2 RF-28 ·
     * plan AD-05): `CardService.preCreate` finds or creates it through the single
     * creation path. The browser used to create it here, fire-and-forget, and a fast
     * save persisted the deal without a customer. A matching account, when one exists,
     * is pre-selected so the form shows it.
     */
    const existingCustomer = this.customers.find((c) => c.contact?._id === contact?._id || c.phone === contact?.phone);

    this.formGroup.patchValue({
      name: contact?.name ?? '',
      description: this.t('kanban.cardForm.descriptionFromAttendance', { name: contact?.name || this.t('kanban.cardForm.customerFallback') }),
      contact: contact?._id ?? null,
      customer: existingCustomer?._id ?? null,
      attendance: this.data.attendance._id,
    }, { emitEvent: false });
  }

  save(): void {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const rawValue = this.formGroup.getRawValue();
    const updatedCard = { ...this.card, ...rawValue, budget: rawValue?.estimatedValue };
    this.card = { ...updatedCard, company: super.getCompanyCurrentUser() };

    const idStage = this.isCardConverting ? rawValue.stage : this.data.idStage;
    const idFunnel = this.isCardConverting ? rawValue.funnel : this.data.idFunnel;

    this.card.stage = <Stage>{ _id: idStage };
    this.card.funnel = <Funnel>{ _id: idFunnel };
    this.card.attachments = this.attachments;

    if (this.isNew && this.isLostStage(idStage)) {
      this.requireLossReasonThenPersist();
      return;
    }

    this.persistCard();
  }

  private isLostStage(idStage: string): boolean {
    return !!this.stagesList.find(stage => stage._id === idStage)?.lostDeal;
  }

  private requireLossReasonThenPersist(): void {
    this.dialog
      // No platform discard here: a deal created straight into a lost stage goes through the
      // generic create, which persists `lossReason` only (E3 ADR-001). Discarding is done on
      // the board, where the move carries the key and the note.
      .open(SelectLossReasonComponent, { width: '700px', disableClose: true, data: <SelectLossReasonData>{ card: this.card, allowPlatform: false } })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: SelectLossReasonResult | undefined) => {
        const idLossReason = result?.idLossReason;
        if (!idLossReason) {
          this.alertService.info(this.t('kanban.cardForm.lossReasonCancelled'));
          return;
        }

        const now = new Date();
        this.card.lossReason = <LossReason>{ _id: idLossReason };
        this.card.won = false;
        this.card.lostDate = now;
        this.card.closedAt = now;

        this.persistCard();
      });
  }

  private persistCard(): void {
    this.loading = true;

    const saveObservable = this.isNew
      ? this.cardService.create(this.card)
      : this.cardService.update(this.data.card._id, this.card);

    saveObservable.pipe(takeUntil(this.destroy$), finalize(() => this.loading = false)).subscribe({
      next: (card: Card) => {
        this.dialogRef.close(card);
      },
      error: (error) => {
        this.alertService.error(error);
      },
      complete: () => {
        this.alertService.success(this.t('kanban.cardForm.savedSuccess'));
      }
    });
  }

  searchByFunnel(funnel: Funnel | null): void {
    this.formGroup.get('stage')?.setValue(null);

    if (!funnel) {
      this.funnelStages = [];
      return;
    }

    // RNF-03: a new deal starts with an empty checklist, so a qualification gate is never offered
    // as where it is born — the API would refuse it.
    this.funnelStages = this.stagesList.filter(stage =>
      String(stage.funnel) === funnel._id && !(this.isNew && stage.kind === StageKindEnum.QUALIFICATION_GATE));
  }

  onAttachmentsSelect(event: any): void {
    const target = event.target;
    const files: FileList = target.files;
    this.file = files[0];

    let reader = new FileReader();
    reader.onload = async (file) => {
      if (this.file) {
        this.loadingSpinnerSubject.next(true);
        const dateMilisecond = new Date().getTime();
        const filename: string = dateMilisecond.toString();

        this.firebaseService.uploadFile(this.file, filename, UploadTypeEnum.TASK).then((snapshot: { url: string }) => {
          this.loadingSpinnerSubject.next(false);
          const currentAttachments = this.formGroup.get('attachment')?.value ?? [];
          this.formGroup.patchValue({ attachment: [...currentAttachments, snapshot.url] });

          const newAttachment: Attachments = {
            filename: this.file.name,
            contentType: this.file.type,
            size: this.file.size,
            url: snapshot.url,
          };

          this.attachments = [...this.attachments, newAttachment];
          this.fileLoaded = true;
        })
          .catch((error) => this.alertService.error(this.t('kanban.cardForm.uploadError')));
      }
    };
    reader.readAsArrayBuffer(this.file);
  }

  removeAttachment(index: number): void {
    const current: string[] = this.formGroup.get('attachment')?.value ?? [];
    const next = current.filter((_, i) => i !== index);

    this.attachments.splice(index, 1);

    this.formGroup.patchValue({ attachment: next });
    this.formGroup.get('attachment')?.markAsDirty();
  }

  createCustomer(): void {
    const dialogRef = this.dialog.open(CustomerQuickFormComponent, {
      width: '600px',
      data: { companyId: super.getCompanyCurrentUser()._id }
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((customerCreated: Customer) => {
      this.getListCustomer();
      if (customerCreated) {
        this.formGroup.get('customer')?.setValue(customerCreated._id);
      }
    });
  }

  createProduct(): void {
    const dialogRef = this.dialog.open(ProductQuickFormComponent, {
      width: '600px',
      data: { companyId: super.getCompanyCurrentUser()._id }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((productCreated: Product) => {
      this.getListProduct();
      if (productCreated) {
        const currentProducts = this.formGroup.get('product')?.value ?? [];
        this.formGroup.get('product')?.setValue([...currentProducts, productCreated]);
      }
    });
  }

  private getListCustomer(): void {
    this.customerService.getList().subscribe({
      next: (list: Array<Customer>) => {
        this.customers = list;
        this.listCustomer = list;
        this.applyCustomerFilter(this.customerControl.value);
      },
    })
  }

  private getListProduct(): void {
    this.productService.getList().subscribe({
      next: (list: Array<Product>) => {
        this.products = list;
        this.listProduct = list;
        this.applyProductFilter(this.productControl.value);
        this.syncSelectedProducts();
      },
    });
  }

  fatchFilterCustomer(): void {
    this.customerControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.applyCustomerFilter(value));
  }

  private setupFilters(): void {
    this.fatchFilterCustomer();
    this.productControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.applyProductFilter(value));
  }

  private setupEstimatedValueAutoFill(): void {
    this.formGroup.get('estimatedValue')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.estimatedValueEdited = true;
        this.formGroup.get('budget')?.setValue(value, { emitEvent: false });
      });

    this.formGroup.get('product')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((products: Array<Product | string>) => {
        if (this.estimatedValueEdited) {
          return;
        }
        this.updateEstimatedValueFromProducts(products);
      });
  }

  private applyCustomerFilter(term: string): void {
    const search = (term ?? '').toString().trim().toLowerCase();
    if (!search) {
      this.listCustomer = [...this.customers];
      return;
    }

    this.listCustomer = this.customers.filter((customer) =>
      (customer.name ?? '').toLowerCase().includes(search)
    );
  }

  private applyProductFilter(term: string): void {
    const search = (term ?? '').toString().trim().toLowerCase();
    if (!search) {
      this.listProduct = [...this.products];
      return;
    }

    this.listProduct = this.products.filter((product) =>
      (product.name ?? '').toLowerCase().includes(search)
    );
  }

  private syncSelectedProducts(): void {
    if (this.isNew) {
      return;
    }

    const cardProducts = this.data.card?.product ?? [];
    const selectedProducts = cardProducts.map((item) => {
      const id = typeof item === 'string' ? item : item._id;
      return this.products.find((prod) => prod._id === id) ?? (typeof item === 'string' ? { _id: id } as Product : item);
    });

    this.formGroup.get('product')?.setValue(selectedProducts);
    this.updateEstimatedValueFromProducts(selectedProducts);
  }

  compareProduct = (a: Product | string | null, b: Product | string | null): boolean => {
    if (!a || !b) {
      return false;
    }

    const idA = typeof a === 'string' ? a : a._id;
    const idB = typeof b === 'string' ? b : b._id;
    return idA === idB;
  };

  private updateEstimatedValueFromProducts(products: Array<Product | string>): void {
    const total = this.calculateProductTotal(products);
    this.setBudgetFields(total);
  }

  private calculateProductTotal(products: Array<Product | string>): number {
    return (products ?? [])
      .map((p: Product | string) => {
        const product = typeof p === 'string' ? this.products.find(prod => prod._id === p) : p;
        return product?.price ?? 0;
      })
      .reduce((a: number, b: number) => a + b, 0);
  }

  private setBudgetFields(value: number): void {
    this.formGroup.get('estimatedValue')?.setValue(value, { emitEvent: false });
    this.formGroup.get('budget')?.setValue(value, { emitEvent: false });
  }

  close(): void {
    this.dialogRef.close();
  }

  isImage(url: string): boolean {
    if (!url) return false;

    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
    const lowerUrl = url.toLowerCase();

    return imageExtensions.some(ext => lowerUrl.includes(ext));
  }

  isPDF(url: string): boolean {
    if (!url) return false;

    return url.toLowerCase().includes('.pdf');
  }

  formatBytes(bytes: number): string {
    const KB = 1024, MB = KB * 1024, GB = MB * 1024;
    if (bytes >= KB) return `${(bytes / KB).toFixed(2)} KB`;
    return `${bytes} B`;
  }
}
